<script setup>
import { ref, onMounted } from "vue";
import Swal from "sweetalert2";
import api from "../../api/axios";
import ArgonButton from "@/components/ArgonButton.vue";
import ArgonInput from "@/components/ArgonInput.vue";

const BACKEND_ROOT = api.defaults.baseURL.replace(/\/api\/?$/, "");

const cargando = ref(true);
const subiendo = ref(false);
const guardando = ref(false);
const guardandoColor = ref(false);
const branding = ref({ name: "", logo_url: null, invoice_prefix: "", invoice_color: "#2f8cf0", next_invoice_number: 1 });
const prefijo = ref("");
const color = ref("#2f8cf0");
const archivoSeleccionado = ref(null);
const previewUrl = ref(null);

async function cargarBranding() {
  const res = await api.get("/company/branding");
  branding.value = res.data.branding;
  prefijo.value = res.data.branding.invoice_prefix || "";
  color.value = res.data.branding.invoice_color || "#2f8cf0";
}

function logoUrlCompleta(url) {
  return url ? `${BACKEND_ROOT}${url}` : null;
}

function onArchivoSeleccionado(event) {
  const file = event.target.files[0];
  if (!file) return;

  archivoSeleccionado.value = file;
  previewUrl.value = URL.createObjectURL(file);
}

async function subirLogo() {
  if (!archivoSeleccionado.value) {
    Swal.fire({ icon: "warning", title: "Seleccioná una imagen primero" });
    return;
  }

  subiendo.value = true;

  const formData = new FormData();
  formData.append("logo", archivoSeleccionado.value);

  try {
    await api.post("/company/logo", formData, { headers: { "Content-Type": "multipart/form-data" } });
    await cargarBranding();
    archivoSeleccionado.value = null;
    previewUrl.value = null;

    Swal.fire({ icon: "success", title: "Logo actualizado", timer: 1500, showConfirmButton: false });

  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo subir el logo", text: err.response?.data?.error || "Error de conexión." });
  } finally {
    subiendo.value = false;
  }
}

async function guardarPrefijo() {
  if (!prefijo.value.trim()) {
    Swal.fire({ icon: "warning", title: "El prefijo no puede estar vacío" });
    return;
  }

  guardando.value = true;

  try {
    await api.put("/company/branding", { invoicePrefix: prefijo.value });
    await cargarBranding();
    Swal.fire({ icon: "success", title: "Prefijo actualizado", timer: 1500, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo guardar el prefijo", text: err.response?.data?.error || "Error de conexión." });
  } finally {
    guardando.value = false;
  }
}

async function guardarColor() {
  guardandoColor.value = true;

  try {
    await api.put("/company/branding", { invoiceColor: color.value });
    await cargarBranding();
    Swal.fire({ icon: "success", title: "Color actualizado", timer: 1500, showConfirmButton: false });
  } catch (err) {
    Swal.fire({ icon: "error", title: "No se pudo guardar el color", text: err.response?.data?.error || "Error de conexión." });
  } finally {
    guardandoColor.value = false;
  }
}

onMounted(async () => {
  cargando.value = true;
  await cargarBranding();
  cargando.value = false;
});
</script>

<template>
  <div class="container-fluid py-4">

    <h4 class="mb-3"><i class="bi bi-receipt me-2"></i>Facturación</h4>

    <div v-if="cargando" class="text-center py-5">
      <div class="spinner-border text-success" role="status"></div>
    </div>

    <div v-else class="card">
      <div class="card-body">
        <p class="text-muted small">
          El logo configurado acá reemplaza automáticamente al logo por defecto tanto en las cotizaciones de viaje (PDF) como en las facturas de reserva. Se ajusta a 105x65px al subirlo, sin deformarse. El prefijo y el color solo aplican a las facturas.
        </p>

        <div class="row g-4">
          <div class="col-md-6">
            <label class="d-block mb-2">Logo de la empresa</label>

            <div class="d-flex align-items-center gap-3 mb-3">
              <div class="logo-preview">
                <img v-if="previewUrl || branding.logo_url" :src="previewUrl || logoUrlCompleta(branding.logo_url)" alt="Logo">
                <span v-else class="text-muted small">Sin logo</span>
              </div>

              <div>
                <input type="file" accept="image/*" class="form-control mb-2" @change="onArchivoSeleccionado">
                <argon-button color="success" size="sm" :disabled="subiendo" @click="subirLogo">
                  <i class="bi bi-upload me-1"></i>Subir logo
                </argon-button>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <label>Prefijo de facturas</label>
            <argon-input v-model="prefijo" placeholder="Ej. MT" style="text-transform: uppercase;" />
            <p class="text-muted small mt-1">
              Próxima factura: <strong :style="{ color: color }">{{ prefijo || "?" }}-{{ String(branding.next_invoice_number).padStart(5, "0") }}</strong>
            </p>
            <argon-button color="success" size="sm" :disabled="guardando" @click="guardarPrefijo">Guardar prefijo</argon-button>
          </div>

          <div class="col-md-6">
            <label class="d-block">Color de la factura</label>
            <p class="text-muted small">Se usa en el consecutivo y el encabezado del PDF.</p>
            <div class="d-flex align-items-center gap-2 mb-2">
              <input type="color" v-model="color" class="color-swatch">
              <argon-input v-model="color" style="max-width: 140px;" />
            </div>
            <argon-button color="success" size="sm" :disabled="guardandoColor" @click="guardarColor">Guardar color</argon-button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<style scoped>
.logo-preview {
  width: 140px;
  height: 90px;
  border: 1px dashed #cbd3da;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.logo-preview img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.color-swatch {
  width: 44px;
  height: 44px;
  padding: 2px;
  border: 1px solid #cbd3da;
  border-radius: 0.5rem;
  cursor: pointer;
}
</style>
